import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const BlogPostsSection = () => {
  const blogPosts = [
    {
      id: 1,
      title: "Navigating the Quantum AI Revolution of 2025",
      excerpt: "How quantum computing and AI are converging to solve previously impossible problems in healthcare, climate science, and materials discovery.",
      image: "https://images.unsplash.com/photo-1488229297570-58520851e868?ixlib=rb-4.0.3&auto=format&fit=crop&w=1169&q=80",
      category: "Quantum AI",
      date: "May 17, 2025",
      readTime: "9 min read",
      author: "Sarah Johnson",
      authorImage: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&auto=format&fit=crop&w=988&q=80",
      color: "rgba(56, 189, 248, 0.8)"
    },
    {
      id: 2,
      title: "Neuromorphic Edge Computing: The New Infrastructure Standard",
      excerpt: "Why brain-inspired computing architectures are revolutionizing data processing at the edge and enabling truly autonomous AI systems.",
      image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&auto=format&fit=crop&w=1172&q=80",
      category: "Edge Computing",
      date: "May 10, 2025",
      readTime: "7 min read",
      author: "Michael Chen",
      authorImage: "https://images.unsplash.com/photo-1556157382-97eda2d62296?ixlib=rb-4.0.3&auto=format&fit=crop&w=940&q=80",
      color: "rgba(139, 92, 246, 0.8)"
    },
    {
      id: 3,
      title: "Synthetic Data Generation: Solving the AI Privacy Paradox",
      excerpt: "How organizations are using advanced synthetic data techniques to train powerful AI models while preserving privacy and meeting global regulations.",
      image: "https://images.unsplash.com/photo-1516110833967-0b5716ca1387?ixlib=rb-4.0.3&auto=format&fit=crop&w=1074&q=80",
      category: "AI Ethics",
      date: "May 3, 2025",
      readTime: "10 min read",
      author: "Priya Sharma",
      authorImage: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&auto=format&fit=crop&w=774&q=80",
      color: "rgba(236, 72, 153, 0.8)"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <section className="blog-posts-section">
      <div className="section-header">
        <motion.h2 
          className="gradient-text"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Latest Insights
        </motion.h2>
        <motion.p 
          className="section-subtext"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          Cutting-edge perspectives on tomorrow's technology trends
        </motion.p>
      </div>

      <motion.div 
        className="blog-posts-container"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
      >
        {blogPosts.map((post) => (
          <motion.article 
            className="blog-post-card glass-morphism"
            key={post.id}
            variants={itemVariants}
            transition={{ duration: 0.6 }}
            whileHover={{ y: -10, transition: { duration: 0.3 } }}
          >
            <div className="post-image-container">
              <div 
                className="post-image-overlay"
                style={{ 
                  background: `linear-gradient(180deg, transparent 0%, ${post.color} 100%)`,
                  opacity: 0.7
                }}
              />
              <motion.img 
                src={post.image} 
                alt={post.title} 
                className="post-image"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.5 }}
              />
              
              <motion.div 
                className="post-category"
                style={{
                  background: post.color,
                  boxShadow: `0 5px 15px ${post.color}`
                }}
                whileHover={{ scale: 1.05 }}
              >
                {post.category}
              </motion.div>
              
              <motion.div 
                className="ai-scan-effect blog-scan"
                animate={{ 
                  top: ['0%', '100%']
                }}
                transition={{ 
                  duration: 2.5, 
                  repeat: Infinity,
                  ease: "linear" 
                }}
              />
            </div>

            <div className="post-content">
              <div className="post-meta">
                <div className="post-meta-item">
                  <span className="meta-icon date-icon">📅</span>
                  <span>{post.date}</span>
                </div>
                <div className="post-meta-item">
                  <span className="meta-icon time-icon">⏱️</span>
                  <span>{post.readTime}</span>
                </div>
              </div>

              <h3 className="post-title">{post.title}</h3>
              <p className="post-excerpt">{post.excerpt}</p>
              
              <div className="post-footer">
                <div className="post-author">
                  <img 
                    src={post.authorImage} 
                    alt={post.author}
                    className="author-avatar" 
                  />
                  <span className="author-name">{post.author}</span>
                </div>
                
                <motion.div
                  className="read-more-link"
                  whileHover={{ scale: 1.05, x: 5 }}
                >
                  <Link to={`/blog/${post.id}`}>
                    Read More
                    <span className="arrow-icon">→</span>
                  </Link>
                </motion.div>
              </div>
              
              <div className="tech-circuit">
                <motion.div 
                  className="circuit-node"
                  animate={{ 
                    boxShadow: [
                      `0 0 5px ${post.color}`,
                      `0 0 10px ${post.color}`,
                      `0 0 5px ${post.color}`
                    ] 
                  }}
                  transition={{ 
                    duration: 1.5, 
                    repeat: Infinity,
                    repeatType: "reverse"
                  }}
                />
                <motion.div className="circuit-line" />
              </div>
            </div>
          </motion.article>
        ))}
      </motion.div>
      
      <motion.div 
        className="blog-cta"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.4 }}
      >
        <Link to="/blog" className="cta-button blog-cta-button">
          <span>View All Articles</span>
          <div className="button-glow"></div>
        </Link>
      </motion.div>
    </section>
  );
};

export default BlogPostsSection;
